import { useState } from "react";
import { YStack, XStack, Text, View, Button } from "tamagui";
import { RiStore2Line, RiMapPinLine, RiRefreshLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import StepLayout from "../StepLayout";
import { PrimaryBtn } from "../Buttons";
import { RECOMMENDATION_POOL, MAX_RECOMMEND } from "../mockData";
import type { Supplier } from "../types";

interface RecommendStepProps {
  onSelect: (s: Supplier) => void;
  onAllRejected: () => void;
  onBack: () => void;
}

/** 步驟 3b：推薦池（單家逐一推薦，最多 MAX_RECOMMEND 家）。 */
const RecommendStep = ({ onSelect, onAllRejected, onBack }: RecommendStepProps) => {
  const [rank, setRank] = useState(0);
  const supplier = RECOMMENDATION_POOL[rank];
  const hasMore = rank < MAX_RECOMMEND - 1;

  const nextOne = () => {
    if (!hasMore) {
      onAllRejected();
      return;
    }
    setRank((r) => r + 1);
  };

  return (
    <StepLayout
      title="為您推薦瓦斯行"
      subtitle={`第 ${rank + 1} 家 · 由系統依距離與評價排序`}
      onBack={onBack}
      step={3}
      footer={
        <YStack gap="$2">
          <XStack gap="$2">
            <View flex={1}>
              <XStack
                backgroundColor={colors.light}
                borderRadius={12}
                borderWidth={1.5}
                borderColor={colors.border}
                p={12}
                ai="center"
                jc="center"
                gap="$1.5"
                cursor={hasMore ? "pointer" : "default"}
                opacity={hasMore ? 1 : 0.5}
                onPress={hasMore ? nextOne : undefined}>
                <RiRefreshLine size={16} color={colors.sub} />
                <Text fontSize={14} fontWeight="600" color={colors.sub}>
                  換一家
                </Text>
              </XStack>
            </View>
            <View flex={1}>
              <XStack
                backgroundColor={colors.light}
                borderRadius={12}
                borderWidth={1.5}
                borderColor={colors.dangerLight}
                p={12}
                ai="center"
                jc="center"
                cursor="pointer"
                onPress={onAllRejected}>
                <Text fontSize={14} fontWeight="600" color={colors.danger}>
                  都不喜歡
                </Text>
              </XStack>
            </View>
          </XStack>
          <PrimaryBtn onPress={() => onSelect(supplier)}>選此家:{supplier.company}</PrimaryBtn>
        </YStack>
      }>
      <YStack gap={14}>
        {/* 推薦卡片 */}
        <YStack background={colors.light} borderRadius={18} overflow="hidden" borderWidth={1} borderColor={colors.borderLight}>
          {/* 上半 */}
          <YStack p="$4" background={colors.primaryBg} borderBottomWidth={1} borderBottomColor={colors.borderLight}>
            <XStack ai="flex-start" gap="$3" mb="$3">
              <View width={56} height={56} borderRadius={14} ai="center" jc="center" className="primary-bg-gradient">
                <RiStore2Line size={26} color={colors.light} />
              </View>
              <YStack flex={1}>
                <Text fontSize={11} color={colors.primary} fontWeight="700" letterSpacing={1} mb="$0.5">
                  推薦 #{rank + 1}
                </Text>
                <Text fontSize={18} fontWeight="800" color={colors.primaryText} mb="$0.5">
                  {supplier.company}
                </Text>
                <Text fontSize={12} color={colors.sub}>
                  {supplier.area}
                </Text>
              </YStack>
            </XStack>

            {/* 距離 */}
            <XStack background={colors.light} borderRadius={10} p="$3" borderWidth={1} borderColor={colors.border} ai="center" gap="$2">
              <View width={28} height={28} borderRadius={8} background={colors.primaryBg} ai="center" jc="center">
                <RiMapPinLine size={16} color={colors.primary} />
              </View>
              <YStack>
                <Text fontSize={10} color={colors.hint} fontWeight="600">
                  距離您的地址
                </Text>
                <Text fontSize={16} fontWeight="800" color={colors.primaryText}>
                  {supplier.distance}
                </Text>
              </YStack>
            </XStack>
          </YStack>

          {/* 下半：店家特色 */}
          <YStack p="$4">
            <Text fontSize={11} color={colors.hint} fontWeight="600" mb="$2">
              店家特色
            </Text>
            <XStack flexWrap="wrap" gap="$1.5">
              {(supplier.tags || []).map((t, i) => (
                <XStack key={i} background={colors.bgGreen} borderWidth={1} borderColor="#BBF7D0" py={4} px={9} borderRadius={20}>
                  <Text fontSize={11} fontWeight="600" color={colors.green}>
                    {t}
                  </Text>
                </XStack>
              ))}
            </XStack>
          </YStack>
        </YStack>

        {/* 進度提示 */}
        <XStack background={colors.light} borderRadius={12} p="$3" borderWidth={1} borderColor={colors.borderLight} ai="center" jc="space-between">
          <Text fontSize={12} color={colors.sub}>
            已看過 <Text fontWeight="800" color={colors.primary}>{rank + 1}</Text> / 最多 {MAX_RECOMMEND} 家
          </Text>
          <XStack gap="$1">
            {Array.from({ length: MAX_RECOMMEND }).map((_, i) => (
              <View key={i} width={6} height={6} borderRadius={3} background={i <= rank ? colors.primary : colors.border} />
            ))}
          </XStack>
        </XStack>
      </YStack>
    </StepLayout>
  );
};

export default RecommendStep;
